import { ffhClient } from './ffh-client';
import {
  describeInvalidPositionCounts,
  isInvalidSquadComposition,
  parseInvalidPositionCounts
} from './squad-composition';
import type { components } from '../../types/api/generated';

export type RateTeamData = components['schemas']['RateTeamResponse']['data'];
export type RateTeamSquadMember = RateTeamData['squad'][number];

/** One suggested transfer, as both endpoints report it. */
export type TeamRatingTopTransfer = NonNullable<RateTeamData['topTransfer']>;

/**
 * The transfer step's outcome, widened with the list of suggestions.
 *
 * `transferSuggestion.transfers` is declared on ffh-public-api#606, which is
 * not deployed, so the generated types here (built from the shipped spec) do
 * not describe it yet. Reading it early is safe: it arrives `undefined` from an
 * API that predates it, and `readSuggestedTransfers` below falls back to the
 * singular field, so this renders one card against the old API and up to three
 * against the new one.
 *
 * Delete this alias and take the field straight off
 * `RateTeamData['transferSuggestion']` once `npm run generate:types` has been
 * re-run against the shipped backend.
 */
type TransferSuggestionWithList = RateTeamData['transferSuggestion'] & {
  transfers?: TeamRatingTopTransfer[];
};

/**
 * The transfers to show, best first.
 *
 * Prefers the list and falls back to the single transfer the API has always
 * sent, which is what lets this deploy in either order against the backend. An
 * empty list is a real answer — the solve named nothing worth showing — so it
 * is only the *absence* of the field that falls back, never a present-but-empty
 * one.
 */
export const readSuggestedTransfers = (
  transferSuggestion: TransferSuggestionWithList | undefined,
  topTransfer: TeamRatingTopTransfer | null | undefined
): TeamRatingTopTransfer[] => {
  const { transfers } = transferSuggestion ?? {};
  if (transfers) return transfers;
  return topTransfer ? [topTransfer] : [];
};

/** Where to collect a suggestion the rating deferred. */
export type SuggestionRun = NonNullable<RateTeamData['suggestionRun']>;

/**
 * What waiting for a deferred suggestion ended in.
 *
 * Every way of ending without a transfer collapses to `unavailable`, because
 * the visitor is already looking at their finished rating and there is nothing
 * to tell them: a solve that named no move, a handle the backend does not
 * recognise, and a wait that ran out all leave the improvement step with
 * nothing to show, which is a state the step has always been able to render.
 */
export type CollectedTransferSuggestion =
  | {
      status: 'suggested';
      /** Every transfer to show, best first. Never empty on this branch. */
      topTransfers: TeamRatingTopTransfer[];
      /** The best of them, for anything still reading a single transfer. */
      topTransfer: TeamRatingTopTransfer;
      potentialRating: number | null;
    }
  | { status: 'unavailable' };

/**
 * How the member assembled the squad being submitted for rating.
 *
 * Diagnostic only: it changes nothing about the rating, the transfer
 * suggestion, or anything the member sees. Only the client can supply it,
 * because a screenshot import hands us the same internal player ids the picker
 * produces, so the two are indistinguishable to the API.
 *
 * The value describes how the squad was assembled, not which screen submitted
 * it, and the assembling action owns it: whatever last put fifteen players into
 * the squad decides the value. A hand-built squad is therefore always PICKER
 * even when a screenshot import came earlier in the session, because the picker
 * starts empty and shares nothing with the discarded import. If the picker is
 * ever seeded from an import, that seeding is what chose the players, so it
 * should keep SCREENSHOT through any later hand edits: the edit is a
 * correction, not a fresh assembly.
 */
export type TeamEntryMethod = 'PICKER' | 'SCREENSHOT';

type RateTeamBody = components['schemas']['RateTeamBody'];

// `entryMethod` is optional on the MANUAL and FPL_PLAYERS request variants, and
// deliberately absent from FPL (we fetch that squad ourselves, so the question
// does not arise). It is declared on ffh-public-api #532, which is not merged,
// so the generated types here (built from the deployed spec) do not describe it
// yet. Sending it early is safe: the request schema is a plain Zod object with
// no `.strict()`, so the deployed API strips unknown keys rather than refusing
// them.
//
// Widening the body type here is the whole of the workaround, so every caller
// stays honestly typed and no component needs a cast. Delete this alias and
// take the field straight off RateTeamBody once `npm run generate:types` has
// been re-run against the shipped backend.
type RateTeamBodyWithEntryMethod = RateTeamBody & {
  entryMethod?: TeamEntryMethod;
};

const postTeamRating = async (body: RateTeamBodyWithEntryMethod) => {
  try {
    return await ffhClient().POST('/team-rating', {
      body,
      // Carries the funnel session cookie, which is the only way the backend can
      // tell that this rating belongs to a funnel waiting to report its
      // completion. A rating asked for outside a funnel sends nothing and is
      // unaffected; the cookie is httpOnly, so nothing here can read it either
      // way. See ffh-public-api#569.
      credentials: 'include'
    });
  } catch (thrown) {
    // Never reaching the API is a different failure from being refused by it,
    // and it arrives here as a raw TypeError rather than an error response.
    throw toNetworkError(thrown);
  }
};

// Captain flags are optional: omit them to let the server auto-pick captain and
// vice from predicted points (used by the GYTR funnel). Supply them to designate
// an explicit captain/vice.
type PlayerWithCaptaincy = {
  id: string;
  isCaptain?: boolean;
  isViceCaptain?: boolean;
};

type FplPlayerWithCaptaincy = {
  fplElementId: number;
  isCaptain?: boolean;
  isViceCaptain?: boolean;
};

/**
 * What kind of thing went wrong, as opposed to what it said.
 *
 * `squad` is the visitor's to fix and routes to the review pitch (FFH-2738).
 * `team` is FPL's: the entry exists but FPL will not serve it yet (typically the
 * gameweek rollover), so retrying cannot help and the funnel offers the other
 * routes in instead. The rest are ours: they decide which explanation the failure state shows and
 * how the failure is grouped in Datadog (FFH-2743).
 */
export type TeamRatingFailureKind =
  | 'squad'
  | 'team'
  | 'network'
  | 'server'
  | 'unknown';

/**
 * A rating that did not come back, carrying enough to act on without reading
 * its message.
 *
 * The message is written for a person and changes with the API's wording, so
 * anything branching on it is one copy edit away from breaking. `kind` and
 * `status` are what the funnel and the error reporting read instead.
 */
export class TeamRatingError extends Error {
  constructor(
    message: string,
    readonly kind: TeamRatingFailureKind,
    readonly status: number | null
  ) {
    super(message);
    this.name = 'TeamRatingError';
  }
}

/**
 * A rating refused because the squad cannot form a legal team.
 *
 * Its own type so a caller can route the visitor to a pitch where they can fix
 * it, and only then: every other failure is one that editing the squad cannot
 * do anything about, and offering the pitch for those is how a dead end becomes
 * a loop (FFH-2738).
 */
export class InvalidSquadError extends TeamRatingError {
  constructor(message: string, status: number | null) {
    super(message, 'squad', status);
    this.name = 'InvalidSquadError';
  }
}

/** Reads the kind off a thrown value, for callers that only have the catch. */
export const readFailureKind = (error: unknown): TeamRatingFailureKind =>
  error instanceof TeamRatingError ? error.kind : 'unknown';

const SERVER_ERROR_FLOOR = 500;

/**
 * A request that never got an answer: no connection, DNS, a blocked origin, an
 * aborted load. `fetch` rejects with a TypeError for all of them, which is the
 * only signal there is - the browser deliberately says no more than that.
 */
const toNetworkError = (thrown: unknown): TeamRatingError =>
  new TeamRatingError(
    thrown instanceof Error && thrown.message
      ? thrown.message
      : 'The connection dropped',
    'network',
    null
  );

const handleTeamRatingError = (response: Response, error: unknown): never => {
  const errorBody = error as { error?: { message?: string } } | undefined;
  const message =
    errorBody?.error?.message ?? 'Failed to calculate team rating';

  const status = response?.status ?? null;
  const kind: TeamRatingFailureKind =
    status !== null && status >= SERVER_ERROR_FLOOR ? 'server' : 'unknown';

  if (status === 404) {
    throw new TeamRatingError(`Team not found: ${message}`, 'team', status);
  }
  if (status === 502) {
    throw new TeamRatingError(`Service unavailable: ${message}`, kind, status);
  }

  // A squad that cannot form a legal team is a problem with what was submitted,
  // and the API names every offending position. Preferring that detail over the
  // sentence it also sends keeps the wording and the units ours; the sentence
  // still stands in wherever the detail is absent.
  const positionalDetail = describeInvalidPositionCounts(
    parseInvalidPositionCounts(error)
  );

  if (isInvalidSquadComposition(error)) {
    throw new InvalidSquadError(positionalDetail ?? message, status);
  }

  throw new TeamRatingError(message, kind, status);
};

export const fetchTeamRatingByFplId = async (fplId: number) => {
  const { data, error, response } = await postTeamRating({
    source: 'FPL',
    fplId
  });

  if (error || !data) {
    return handleTeamRatingError(response, error);
  }

  return data.data;
};

export const fetchTeamRatingByPlayerIds = async (
  players: PlayerWithCaptaincy[],
  entryMethod?: TeamEntryMethod
) => {
  const { data, error, response } = await postTeamRating({
    source: 'MANUAL',
    players,
    ...(entryMethod && { entryMethod })
  });

  if (error || !data) {
    return handleTeamRatingError(response, error);
  }

  return data.data;
};

export const fetchTeamRatingByFplElementIds = async (
  players: FplPlayerWithCaptaincy[],
  entryMethod?: TeamEntryMethod
) => {
  const { data, error, response } = await postTeamRating({
    source: 'FPL_PLAYERS',
    players,
    ...(entryMethod && { entryMethod })
  });

  if (error || !data) {
    return handleTeamRatingError(response, error);
  }

  return data.data;
};

const RATE_LIMITED_STATUS = 429;
const SERVER_ERROR_STATUS = 500;
const CLIENT_ERROR_STATUS = 400;

/**
 * How long to wait for a deferred suggestion before giving up on it.
 *
 * The backend abandons a run at ten minutes, so this only bites when no answer
 * arrives at all. Two minutes is many times the measured solve time and matches
 * the bound the member path uses, and it keeps a wedged solve from holding the
 * improvement step on a working state for as long as the backend is willing to
 * keep trying.
 */
const MAX_SUGGESTION_WAIT_MS = 2 * 60 * 1000;

/**
 * How much further apart to space attempts the backend refused to serve.
 *
 * Only reached when it answers 429 or fails outright, neither of which states a
 * pace of its own. Everything else waits exactly as long as it was told to.
 */
const REFUSED_ATTEMPT_BACKOFF_MULTIPLIER = 2;
const MAX_REFUSED_ATTEMPT_BACKOFF_MS = 30 * 1000;

/**
 * Why a wait ended early, as something throwable.
 *
 * `AbortSignal.reason` carries the cancellation where the environment supplies
 * it, but not every one does, and rejecting with whatever it holds means
 * rejecting with `undefined` on those.
 */
const ABANDONED_MESSAGE = 'The transfer suggestion was no longer needed';

const toAbortReason = (signal?: AbortSignal): unknown =>
  signal?.reason ?? new Error(ABANDONED_MESSAGE);

const wait = (milliseconds: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(toAbortReason(signal));
      return;
    }
    const timeout = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, milliseconds);
    const onAbort = () => {
      clearTimeout(timeout);
      reject(toAbortReason(signal));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

type ReadSuggestionOutcome =
  | { status: 'collected'; suggestion: CollectedTransferSuggestion }
  | { status: 'working'; retryAfterMilliseconds: number }
  /** Refused rather than answered, so worth asking again more slowly. */
  | { status: 'refused' }
  /** Nothing will ever come of this handle. */
  | { status: 'unavailable' };

/**
 * Asks once whether a deferred suggestion is ready.
 *
 * The handle is the whole of this endpoint's authorisation, so it travels in
 * the path and nowhere else: it is never logged, reported or measured.
 *
 * Given its own deadline as well as the caller's signal, because a request that
 * is never answered is otherwise unbounded: the loop around this can stop
 * starting attempts on time and still be held open by the last one it started.
 */
const readTransferSuggestion = async ({
  suggestionId,
  timeoutMilliseconds,
  signal
}: {
  suggestionId: string;
  timeoutMilliseconds: number;
  signal?: AbortSignal;
}): Promise<ReadSuggestionOutcome> => {
  // Ends this attempt for either reason, so the request below needs to know
  // about only one of them.
  const attempt = new AbortController();
  const endAttempt = () => attempt.abort();
  const timeout = setTimeout(endAttempt, timeoutMilliseconds);
  signal?.addEventListener('abort', endAttempt, { once: true });

  let data;
  let error;
  let response;

  try {
    ({ data, error, response } = await ffhClient().GET(
      '/team-rating/suggestions/{suggestionId}',
      { params: { path: { suggestionId } }, signal: attempt.signal }
    ));
  } catch (thrown) {
    // Being abandoned is the one failure that must not be absorbed: the caller
    // has stopped caring and is entitled to hear so.
    if (signal?.aborted) {
      throw toAbortReason(signal);
    }

    // Anything else is a connection that dropped or an attempt that outlived
    // its slice of the deadline. Neither is evidence about the solve, so both
    // are read the same way as a backend that refused to answer: ask again,
    // further apart, for as long as there is time left. Absorbing it here is
    // what keeps this function's promise that only abandonment rejects — left
    // to propagate, it reaches the caller looking exactly like abandonment,
    // and a caller that cannot tell them apart leaves its pending state set.
    return { status: 'refused' };
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', endAttempt);
  }

  const status = response?.status ?? 0;

  // Both a refusal to serve and a failure to answer are about the backend's
  // state rather than this suggestion's, so neither is evidence the solve
  // ended. Asking again, further apart, is the only reading that does not
  // throw away an answer that is still coming.
  if (status === RATE_LIMITED_STATUS || status >= SERVER_ERROR_STATUS) {
    return { status: 'refused' };
  }

  // 404 above all: the contract reserves it for a handle naming no suggestion
  // of ours, which asking again cannot change.
  if (error || !data || status >= CLIENT_ERROR_STATUS) {
    return { status: 'unavailable' };
  }

  // Read by shape rather than by status, as the member path does. The pending
  // reply is the only one carrying a run, because it is the only one with a
  // next attempt to describe.
  if ('suggestionRun' in data.data) {
    return {
      status: 'working',
      retryAfterMilliseconds: data.data.suggestionRun.retryAfterMilliseconds
    };
  }

  const { topTransfer, potentialRating, transferSuggestion } = data.data;
  const topTransfers = readSuggestedTransfers(transferSuggestion, topTransfer);
  const [best] = topTransfers;

  return {
    status: 'collected',
    suggestion: best
      ? {
          status: 'suggested',
          topTransfers,
          topTransfer: best,
          potentialRating
        }
      : { status: 'unavailable' }
  };
};

/**
 * Waits for a suggestion the rating deferred, at the pace the backend sets.
 *
 * Resolves only once there is nothing further to wait for, so a caller's
 * pending state covers the whole wait. Rejects only when abandoned: every other
 * ending is an outcome the improvement step can render.
 *
 * Guests have no access token and so no channel to be told on, which is why
 * this asks repeatedly where the member path waits to be called. The interval
 * is always the one the backend just quoted, never one chosen here, so a busy
 * queue can slow every waiting visitor at once.
 */
export const collectTransferSuggestion = async ({
  suggestionId,
  retryAfterMilliseconds,
  maxWaitMilliseconds = MAX_SUGGESTION_WAIT_MS,
  signal
}: SuggestionRun & {
  /** How long to keep asking. Defaults to the bound described above. */
  maxWaitMilliseconds?: number;
  signal?: AbortSignal;
}): Promise<CollectedTransferSuggestion> => {
  const giveUpAt = Date.now() + maxWaitMilliseconds;
  let waitMilliseconds = retryAfterMilliseconds;

  for (;;) {
    // Never wait past the point this gives up. The quoted interval carries no
    // upper bound in the contract, so an unclamped wait would overrun the
    // deadline and then still ask, making the limit a suggestion rather than a
    // limit.
    const remainingBeforeWait = giveUpAt - Date.now();
    if (remainingBeforeWait <= 0) break;
    await wait(Math.min(waitMilliseconds, remainingBeforeWait), signal);

    // Whatever is left is what the attempt itself gets, so the deadline bounds
    // the whole wait rather than only the decision to start another attempt.
    const remainingForAttempt = giveUpAt - Date.now();
    if (remainingForAttempt <= 0) break;

    const outcome = await readTransferSuggestion({
      suggestionId,
      timeoutMilliseconds: remainingForAttempt,
      signal
    });

    if (outcome.status === 'collected') {
      return outcome.suggestion;
    }

    if (outcome.status === 'unavailable') {
      return { status: 'unavailable' };
    }

    waitMilliseconds =
      outcome.status === 'working'
        ? outcome.retryAfterMilliseconds
        : Math.min(
            waitMilliseconds * REFUSED_ATTEMPT_BACKOFF_MULTIPLIER,
            MAX_REFUSED_ATTEMPT_BACKOFF_MS
          );
  }

  // Out of patience rather than out of answers. The rating is already on
  // screen and final, so the improvement step simply has nothing to add.
  return { status: 'unavailable' };
};
